import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, message } from 'antd';
import { c212Api, FC210 } from '../../services/c212Api';
import { useC212Mode } from './hooks/useC212Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useC212Record } from './hooks/useC212Record';
import { useC212Navigation } from './hooks/useC212Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1ForecastDetail from './components/Tab1ForecastDetail';
import Tab2PurchaseReply from './components/Tab2PurchaseReply';
import Tab3Attachments from './components/Tab3Attachments';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import { getStatusBadge, dateToInput, inputToDate } from './helpers';
import './C212Form.css';

const TAB_LABELS = ['胚布預估', '採購回應', '備註/附件'];

const C212Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = useModulePermission(Modules.C212);
  const { mode, setMode, isEditing } = useC212Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details, attachments,
    selectedDetailRow, selectedAttachRow, loading,
    setSelectedDetailRow, setSelectedAttachRow,
    setDetails, setAttachments,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    addAttachRow, deleteAttachRow,
    revertToRecord, resetForAdd, applyResult,
  } = useC212Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useC212Navigation(loadRecordWithNames);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewId) {
          const idx = ids.indexOf(reviewId);
          if (idx >= 0) setCurrentIdx(idx);
          await loadRecordWithNames(reviewId);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load C212 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mode Actions [T-F07: dual-state buttons]
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: FC210) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fc210_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        resolveAllNames(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, resolveAllNames, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      if (record) resolveAllNames(record);
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, resolveAllNames, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除預估單 ${record.fc210_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => { navTo(idx, isEditing); }, [navTo, isEditing]);
  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    message.info('列印功能待實作');
  }, [record, isEditing]);

  // -- Approval Handlers --
  const handleSubmit = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將預估單 ${record.fc210_01} 送審？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await c212Api.submitForApproval(record.fc210_01);
          message.success(resp.message || '送審成功');
          await loadRecordWithNames(record.fc210_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleApprove = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准預估單 ${record.fc210_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await c212Api.approve(record.fc210_01);
          message.success(resp.message || '核准成功');
          await loadRecordWithNames(record.fc210_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleReject = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回預估單 ${record.fc210_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await c212Api.reject(record.fc210_01);
          message.success(resp.message || '退回成功');
          await loadRecordWithNames(record.fc210_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const handleWriteoff = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fc210_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await c212Api.writeoff(record.fc210_01);
          message.success(resp.message || '沖銷成功');
          await loadRecordWithNames(record.fc210_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecordWithNames]);

  const [showHistory, setShowHistory] = useState(false);

  const statusBadge = getStatusBadge(record);

  return (
    <PermissionGuard module={Modules.C212} action="query">
    <div className="c212-form">
      {loading && <div className="loading-overlay">載入中...</div>}
      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <span className="current">胚布預估單</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>C212-胚布預估單</h1>
            <span className="badge badge-default">C212</span>
            {statusBadge && <span className={`badge ${statusBadge.cls}`}>{statusBadge.text}</span>}
          </div>
        </div>

        {/* Search Bar + Master Header Fields */}
        <div className="search-bar">
          <label>單號</label>
          <input type="text" value={String(getField('fc210_01') || '')} disabled style={{ width: 100 }} />
          <label>日期</label>
          <input type="date"
            value={dateToInput(getField('fc210_11') as string | null)}
            onChange={e => setField('fc210_11', inputToDate(e.target.value) as FC210[keyof FC210])}
            disabled={!isEditing} style={{ width: 140 }} />
          <label>快速查詢</label>
          <input
            type="text"
            placeholder="輸入預估單號"
            disabled={isEditing}
            style={{ width: 140 }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                const val = (e.target as HTMLInputElement).value.trim();
                if (val) {
                  await loadRecordWithNames(val);
                  const idx = allIds.indexOf(val);
                  if (idx >= 0) setCurrentIdx(idx);
                }
              }
            }}
          />
          <span style={{ fontSize: 12, color: '#999' }}>
            {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '無資料'}
          </span>
        </div>

        {/* Tabs (3 pages as per Pageframe1) */}
        <div className="tabs">
          <div className="tab-nav">
            {TAB_LABELS.map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1ForecastDetail
              formData={formData} names={names} details={details}
              selectedDetailRow={selectedDetailRow} isEditing={isEditing}
              getField={getField} setField={setField}
              setDetails={setDetails} setSelectedDetailRow={setSelectedDetailRow}
              addDetailRow={addDetailRow} deleteDetailRow={deleteDetailRow}
              resolveSingleName={resolveSingleName} />
          </div>
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2PurchaseReply
              formData={formData} names={names} isEditing={isEditing}
              getField={getField} setField={setField} />
          </div>
          <div className={`tab-pane ${activeTab === 2 ? 'active' : ''}`}>
            <Tab3Attachments
              formData={formData} attachments={attachments}
              selectedAttachRow={selectedAttachRow} isEditing={isEditing}
              getField={getField} setField={setField}
              setAttachments={setAttachments} setSelectedAttachRow={setSelectedAttachRow}
              addAttachRow={addAttachRow} deleteAttachRow={deleteAttachRow} />
          </div>
        </div>

        {/* Bottom Action Bar (SharedActionBar) */}
        {(() => {
          const s80 = (formData.fc210_80 || '').trim();
          const approval: ApprovalState = {
            statusCode: s80,
            isApproved: s80 === 'P',
            isPending: ['D', 'S'].includes(s80), // 統一碼 D + 舊碼 S 過渡容忍
            isLocked: ['P', 'D', 'S'].includes(s80),
          };
          return (
            <SharedActionBar
              mode={mode} isEditing={isEditing} hasRecord={!!record}
              currentIdx={currentIdx} recordCount={allIds.length}
              onNavFirst={() => handleNavTo(0)} onNavPrev={() => handleNavTo(currentIdx - 1)}
              onNavNext={() => handleNavTo(currentIdx + 1)} onNavLast={() => handleNavTo(allIds.length - 1)}
              onAdd={handleAddAction} onModify={handleModifyAction} onDelete={handleDeleteAction}
              onSave={handleAddAction} onCancel={handleModifyAction}
              approval={approval}
              onSubmit={handleSubmit} onApprove={handleApprove}
              onReject={handleReject} onWriteoff={handleWriteoff} onHistory={() => setShowHistory(true)}
              onPrint={handlePrint}
              onExit={() => navigate('/erp')}
              canCreate={perms.canCreate} canEdit={perms.canEdit}
              canDelete={perms.canDelete} canApprove={perms.canApprove}
              canPrint={perms.canPrint}
            />
          );
        })()}
      </div>
    </div>
    </PermissionGuard>
  );
};

export default C212Form;
